/**
 * playbackStore – reaktive Fassade über der geteilten Audio-Engine (`composables/osmd/playbackEngine.ts`)
 * plus der `IPlaybackListener`, den jeder `PlaybackManager` bekommt.
 *
 * Bewusst KEIN `console.debug` in den Listener-Callbacks (kritisch – nicht wieder einführen): der
 * osmd-extended Scheduler ist eine `setTimeout`-Kette ohne Lookahead, Noten werden bei „jetzt"
 * geplant. `cursorPositionChanged`/`notesPlaybackEventOccurred` feuern pro Note bzw. Beat auf dem
 * Main-Thread – jede Ausgabe lebender OSMD-Objekte (DevTools serialisiert sie) wird direkt hörbare
 * Verspätung. Die SoundFont-Bereitschaft kommt nicht aus `soundLoaded`/`allSoundsLoaded` (die feuern
 * nur für den jeweiligen Manager), sondern aus der Engine.
 */
import {computed, ref} from 'vue';
import {useLocalStorage} from '@vueuse/core';
import {defineStore} from 'pinia';
import type {Fraction, IPlaybackListener, MidiInstrument} from 'opensheetmusicdisplay';
import type {CursorPosChangedData} from 'opensheetmusicdisplay/build/dist/src/Common/DataObjects/CursorPosChangedData';
import {getSharedAudioPlayer, type SharedAudioPlayer, type SoundFontState} from '@/composables/osmd/playbackEngine';

/**
 * Gemerkte Spur-Lautstärken – **eigener localStorage-Schlüssel**, wie `griffschrift:removeBass`.
 * Bewusst NICHT Teil von `ConversionSettings`: eine Abhör-Vorliebe hat im Convert-Request nichts
 * verloren.
 */
const TRACK_VOLUME_STORAGE_KEY = 'griffschrift:trackVolumes';

export const usePlaybackStore = defineStore('playback', () => {
    /** Vom aktuellen/letzten Sheet benötigte SoundFonts (Core + Instrumente). */
    const requiredInstruments = ref<MidiInstrument[]>([]);
    const loadedInstruments = ref<MidiInstrument[]>([]);
    const loadingInstruments = ref<MidiInstrument[]>([]);
    const soundLoadError = ref<string | null>(null);
    /** Taktindex des Playback-Cursors (Konsument: PlaybackControlsOverlay positioniert die Player-Controls). */
    const playbackMeasureIndex = ref(0);

    /**
     * Lautstärke je Spur, **nach Spurname** (`Harmonika-Bass`, `Melodie`, …).
     *
     * Nicht nach Index: `Instrument.Id` ist eine laufende Nummer über die `<score-part>`-Elemente
     * und verschiebt sich, sobald ein Part dazukommt oder wegfällt – genau das tut das
     * Bass-Vorschau-System bei jedem Erzeugen/Entfernen. Der Name bleibt stabil.
     *
     * Nur die Lautstärke wird gemerkt: Stumm und Solo sind Griffe für den Moment, keine Vorlieben.
     */
    const trackVolumes = useLocalStorage<Record<string, number>>(TRACK_VOLUME_STORAGE_KEY, () => ({}));

    function rememberTrackVolume(name: string, volume: number): void {
        if (!name) return;
        trackVolumes.value = {...trackVolumes.value, [name]: volume};
    }

    function rememberedTrackVolume(name: string): number | undefined {
        const value = trackVolumes.value[name];
        return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
    }

    const isSoundReady = computed(() =>
        requiredInstruments.value.length > 0
        && requiredInstruments.value.every((id) => loadedInstruments.value.includes(id)),
    );
    const isLoadingSounds = computed(() =>
        requiredInstruments.value.some((id) => loadingInstruments.value.includes(id)),
    );
    const soundLoadLabel = computed(() => {
        const total = requiredInstruments.value.length;
        const loaded = requiredInstruments.value.filter((id) => loadedInstruments.value.includes(id)).length;
        return `SoundFonts werden geladen … (${loaded}/${total})`;
    });

    // Engine lazy anbinden: der Konstruktor legt den (einzigen) AudioContext an – erst, wenn eine
    // Vorschau ihn wirklich braucht, nicht beim Store-Setup.
    let engine: SharedAudioPlayer | null = null;

    function applyEngineState(state: SoundFontState): void {
        loadedInstruments.value = [...state.loaded];
        loadingInstruments.value = [...state.loading];
        const firstError = state.failed.values().next();
        soundLoadError.value = firstError.done ? null : firstError.value;
    }

    function getEngine(): SharedAudioPlayer {
        if (!engine) {
            engine = getSharedAudioPlayer();
            engine.subscribe(applyEngineState);
            applyEngineState(engine.getState());
        }
        return engine;
    }

    /** Merkt die Instrumente als benötigt und lädt fehlende; löst auf, sobald alle geladen (oder
     *  fehlgeschlagen) sind. Fehlgeschlagene werden bei erneutem Aufruf erneut versucht. */
    async function ensureSoundsLoaded(ids: readonly MidiInstrument[]): Promise<void> {
        const required = new Set(requiredInstruments.value);
        for (const id of ids) required.add(id);
        requiredInstruments.value = [...required];
        const player = getEngine();
        const missing = ids.filter((id) => !player.isReady([id]));
        if (missing.length > 0) await player.preload(missing);
    }

    /** AudioContext innerhalb der User-Geste fortsetzen (Autoplay-Policy). */
    function resumeAudio(): Promise<void> {
        return getEngine().resume();
    }

    /** Kurzer Orientierungston beim Bearbeiten/Auswählen (Oszillator, lädt KEINE SoundFonts nach –
     *  siehe `osmd/notePreviewTone.ts`). */
    function previewPitches(midiKeys: readonly number[]): Promise<void> {
        return getEngine().previewPitches(midiKeys);
    }

    const playbackListener: IPlaybackListener = {
        cursorPositionChanged: (_timestamp: Fraction, data: CursorPosChangedData) => {
            if (playbackMeasureIndex.value !== data.CurrentMeasureIndex) {
                playbackMeasureIndex.value = data.CurrentMeasureIndex;
            }
        },
        pauseOccurred: () => {
            playbackMeasureIndex.value = 0;
        },
        resetOccurred: () => {
            playbackMeasureIndex.value = 0;
        },
        selectionEndReached: () => {
        },
        notesPlaybackEventOccurred: () => {
        },
        metronomeSoundOccurred: () => {
        },
        soundLoaded: () => {
        },
        allSoundsLoaded: () => {
        },
    };

    return {
        requiredInstruments,
        loadedInstruments,
        loadingInstruments,
        soundLoadError,
        isSoundReady,
        isLoadingSounds,
        soundLoadLabel,
        playbackMeasureIndex,
        trackVolumes,
        rememberTrackVolume,
        rememberedTrackVolume,
        ensureSoundsLoaded,
        resumeAudio,
        previewPitches,
        getPlaybackListener: () => playbackListener,
    };
});
